import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Flame,
  LayoutDashboard,
  Map as MapIcon,
  Inbox as InboxIcon,
  MoreHorizontal,
  Users,
  Shield,
  BarChart3,
  Sun,
  User as UserIcon,
  Building2,
  Droplets,
  Images,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { TenantSwitcher } from './TenantSwitcher';
import { cn } from '@/lib/utils';

interface NavCell {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Highlight a primary report-style cell. */
  primary?: boolean;
  /** Function to check whether this cell is "active" for the given pathname. */
  isActive?: (pathname: string) => boolean;
}

const cells: NavCell[] = [
  { to: '/dashboard', label: 'Home', icon: LayoutDashboard },
  {
    to: '/wildfires?view=map',
    label: 'Map',
    icon: MapIcon,
    isActive: (p) => p.startsWith('/wildfires') && !p.startsWith('/wildfires/new'),
  },
  { to: '/wildfires/new', label: 'Report', icon: Flame, primary: true },
  { to: '/forecast', label: 'Forecast', icon: Sun },
];

interface MoreLink {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Capability gate — hidden unless can(resource, action). */
  requires?: { resource: string; action: string };
  /** Admin-console link — shown to admin/super-admin only. */
  admin?: boolean;
}

const moreLinks: MoreLink[] = [
  { to: '/inbox', label: 'Inbox', icon: InboxIcon, requires: { resource: 'image', action: 'moderate' } },
  { to: '/members', label: 'Members', icon: Users, requires: { resource: 'fpa_member', action: 'list' } },
  { to: '/firefighters', label: 'Firefighters', icon: Shield, requires: { resource: 'firefighter', action: 'list' } },
  { to: '/water-sources', label: 'Water sources', icon: Droplets, requires: { resource: 'water_source', action: 'list' } },
  { to: '/fpa-gallery', label: 'Gallery', icon: Images, requires: { resource: 'image', action: 'list' } },
  { to: '/stats', label: 'Statistics', icon: BarChart3, requires: { resource: 'reporting', action: 'read' } },
  { to: '/admin', label: 'Admin settings', icon: Building2, admin: true },
  { to: '/profile', label: 'Profile', icon: UserIcon },
];

export function MobileBottomNav() {
  const { isAuthenticated, isAdmin, isSuperAdmin, can, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!isAuthenticated) return null;

  const handleLogout = async () => {
    setMoreOpen(false);
    await logout();
    navigate('/auth/login');
  };

  const visibleMoreLinks = moreLinks.filter((link) => {
    if (link.admin) return isAdmin() || isSuperAdmin();
    if (link.requires) return can(link.requires.resource, link.requires.action);
    return true;
  });

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="grid grid-cols-5 items-end">
        {cells.map((cell) => (
          <NavCellLink key={cell.to} cell={cell} pathname={pathname} />
        ))}

        <li className="flex justify-center">
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex w-full flex-col items-center gap-1 py-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <MoreHorizontal className="h-5 w-5" />
                More
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>More</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-1">
                {visibleMoreLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMoreOpen(false)}
                      className="flex items-center gap-3 rounded-md px-3 py-3 text-sm hover:bg-muted"
                    >
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      {link.label}
                    </Link>
                  );
                })}
              </div>
              <Separator className="my-4" />
              <div className="space-y-2">
                <p className="px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  FPA scope
                </p>
                <div className="px-3">
                  <TenantSwitcher />
                </div>
              </div>
              <Separator className="my-4" />
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}

function NavCellLink({ cell, pathname }: { cell: NavCell; pathname: string }) {
  const Icon = cell.icon;
  const active = cell.isActive ? cell.isActive(pathname) : pathname === cell.to.split('?')[0];

  if (cell.primary) {
    return (
      <li className="flex justify-center">
        <NavLink to={cell.to} className="flex flex-col items-center gap-1 py-2 text-xs">
          <span className="-mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
            <Icon className="h-6 w-6" />
          </span>
          <span className={cn(active ? 'text-primary font-medium' : 'text-muted-foreground')}>
            {cell.label}
          </span>
        </NavLink>
      </li>
    );
  }

  return (
    <li className="flex justify-center">
      <NavLink
        to={cell.to}
        className={cn(
          'flex w-full flex-col items-center gap-1 py-2 text-xs',
          active ? 'text-primary font-medium' : 'text-muted-foreground',
        )}
      >
        <Icon className="h-5 w-5" />
        {cell.label}
      </NavLink>
    </li>
  );
}

export default MobileBottomNav;
